import { menuCategoryAdapter, menuItemAdapter } from '@/redux/menu/schema'
import { injectedOmsApi } from '@/services/oms'
import { createSlice, EntityState, PayloadAction } from '@reduxjs/toolkit'

type Nullable<T> = T | null

type PlainInfo<T> = {
  status: RequestState
  error?: Nullable<string>
  data: Nullable<T>
  categoryEntities: EntityState<Oms.MenuCategory, string>
  itemEntities: EntityState<Oms.MenuItem, string>
}

type RequestState = 'pending' | 'fulfilled' | 'rejected' | 'none'

const initialState: PlainInfo<Oms.MenuCategory[]> = {
  status: 'none',
  data: [],
  error: null,
  categoryEntities: menuCategoryAdapter.getInitialState(),
  itemEntities: menuItemAdapter.getInitialState(),
}

// Shared by the RTK Query matcher (real fetches) and setMenuData (SSR seed):
// when the store is seeded via upsertQueryEntries no fetch ever happens, so
// matchFulfilled never fires — without an explicit seed action this slice
// (and everything reading itemEntities, e.g. ProductDetailsModal deep links)
// would stay empty.
const populateFromMenuResponse = (
  state: PlainInfo<Oms.MenuCategory[]>,
  payload: Oms.ApiResponse<{ tags: Oms.MenuCategory[] }>,
) => {
  state.status = 'fulfilled'
  state.data = payload.payload.tags
  if (payload.payload?.tags) {
    state.categoryEntities = menuCategoryAdapter.upsertMany(
      state.categoryEntities,
      payload.payload.tags,
    )
    state.itemEntities = menuItemAdapter.upsertMany(
      state.itemEntities,
      payload.payload.tags.reduce(
        (arr, category) => [
          ...arr,
          ...category.items.map((item) => ({
            ...item,
            categoryId: category.id,
          })),
        ],
        [] as Oms.MenuItem[],
      ),
    )
  }
}

export const menuSlice = createSlice({
  name: '@menu',
  initialState,
  reducers: {
    setMenuData: (
      state,
      action: PayloadAction<Oms.ApiResponse<{ tags: Oms.MenuCategory[] }>>,
    ) => {
      populateFromMenuResponse(state, action.payload)
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      injectedOmsApi.endpoints.getMenu.matchFulfilled,
      (state, action) => {
        populateFromMenuResponse(state, action.payload)
      },
    )
  },
})

export const { setMenuData } = menuSlice.actions
